import { MessageSquare } from "lucide-react";
import { Tooltip, TooltipContent, TooltipTrigger } from "~/components/ui/tooltip";
import { cn } from "~/lib/utils";
import { AgentAvatar } from "~/pages/manifold-nextgen/components/agents/AgentAvatar";
import {
  UNAVAILABLE_AGENT_LABEL,
  SessionAgentSlot,
} from "~/pages/manifold-nextgen/components/projects/sessionAgentSlots";

export interface AgentAvatarStackProps {
  slots: SessionAgentSlot[];
  size?: number;
  max?: number;
  className?: string;
}

/** Overlapping agent avatars with one combined tooltip, for a session served by
 * more than one agent. Mirrors `AvatarStack`'s overlap/overflow DOM, but renders
 * the Dicebear `AgentAvatar` (matching the single-agent look) and merges every
 * name into a single tooltip instead of one per avatar. A slot for an agent no
 * longer on the project roster renders the generic chat icon in a muted circle
 * instead of an avatar. */
export function AgentAvatarStack({ slots, size = 18, max = 2, className }: AgentAvatarStackProps) {
  const shown = slots.slice(0, max);
  const overflow = slots.length - shown.length;

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span className={cn("inline-flex shrink-0 items-center", className)}>
          {shown.map((slot, i) =>
            slot.kind === "agent" ? (
              <AgentAvatar
                key={slot.agent.id}
                agent={slot.agent}
                size={size}
                className={cn("ring-2 ring-card", i > 0 && "-ml-1.5")}
                aria-hidden="true"
              />
            ) : (
              <span
                key={slot.id}
                className={cn(
                  "relative inline-flex shrink-0 items-center justify-center rounded-full bg-muted text-muted-foreground ring-2 ring-card",
                  i > 0 && "-ml-1.5"
                )}
                style={{ width: size, height: size }}
                aria-hidden="true"
              >
                <MessageSquare size={size - 6} />
              </span>
            )
          )}
          {overflow > 0 && (
            <span
              className="relative -ml-1.5 inline-flex shrink-0 items-center justify-center rounded-full bg-muted text-[10px] font-medium text-muted-foreground ring-2 ring-card"
              style={{ width: size, height: size }}
            >
              +{overflow}
            </span>
          )}
        </span>
      </TooltipTrigger>
      <TooltipContent>
        <ul className="space-y-0.5">
          {slots.map((slot) => (
            <li key={slot.kind === "agent" ? slot.agent.id : slot.id}>
              {slot.kind === "agent" ? slot.agent.display_name : UNAVAILABLE_AGENT_LABEL}
            </li>
          ))}
        </ul>
      </TooltipContent>
    </Tooltip>
  );
}
